import React, { useState } from 'react';
import styled from 'styled-components';
import { Plus, Trash2 } from 'lucide-react';
import menuApi from 'api/requests/menu';
import colors from 'theme/colors';
import { panelAccent } from 'theme/panelAccent';

export default function OptionGroupForm({ slug, initialData, onSaved, onCancel }) {
    const [name, setName] = useState(initialData?.name || '');
    const [isRequired, setIsRequired] = useState(initialData?.is_required ?? false);
    const [minSelections, setMinSelections] = useState(initialData?.min_selections ?? 0);
    const [maxSelections, setMaxSelections] = useState(initialData?.max_selections ?? 1);
    const [sortOrder, setSortOrder] = useState(initialData?.sort_order ?? '');
    const [options, setOptions] = useState(
        initialData
            ? (initialData.options || []).map(o => ({
                id: o.id,
                name: o.name,
                price_adjustment: String(o.price_adjustment || '0'),
              }))
            : [{ name: '', price_adjustment: '0' }]
    );
    const [deletedOptionIds, setDeletedOptionIds] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    function addOptionRow() {
        setOptions(prev => [...prev, { name: '', price_adjustment: '0' }]);
    }

    function updateOptionField(index, field, value) {
        setOptions(prev => prev.map((o, i) => i === index ? { ...o, [field]: value } : o));
    }

    function removeOption(index) {
        const opt = options[index];
        if (opt.id) setDeletedOptionIds(prev => [...prev, opt.id]);
        setOptions(prev => prev.filter((_, i) => i !== index));
    }

    async function handleSubmit(e) {
        e.preventDefault();
        if (!name.trim()) { setError('Nome é obrigatório'); return; }
        setLoading(true);
        setError(null);
        try {
            const payload = {
                name: name.trim(),
                is_required: isRequired,
                min_selections: Number(minSelections),
                max_selections: Number(maxSelections),
                ...(sortOrder !== '' && { sort_order: Number(sortOrder) }),
                ...(!initialData && {
                    options: options
                        .filter(o => o.name.trim())
                        .map(o => ({
                            name: o.name.trim(),
                            price_adjustment: parseFloat(o.price_adjustment) || 0,
                        })),
                }),
            };
            let saved;
            if (initialData) {
                const { data } = await menuApi.updateOptionGroup(slug, initialData.id, payload);
                saved = data;
                await Promise.all([
                    ...deletedOptionIds.map(id => menuApi.deleteOption(id)),
                    ...options
                        .filter(o => o.id)
                        .map(o => menuApi.updateOption(o.id, {
                            name: o.name.trim(),
                            price_adjustment: parseFloat(o.price_adjustment) || 0,
                        })),
                    ...options
                        .filter(o => !o.id && o.name.trim())
                        .map(o => menuApi.createOption(slug, initialData.id, {
                            name: o.name.trim(),
                            price_adjustment: parseFloat(o.price_adjustment) || 0,
                        })),
                ]);
            } else {
                const { data } = await menuApi.createOptionGroup(slug, payload);
                saved = data;
            }
            onSaved(saved);
        } catch {
            setError('Erro ao salvar. Tente novamente.');
        } finally {
            setLoading(false);
        }
    }

    return (
        <Form onSubmit={handleSubmit}>
            <Field>
                <Label>Nome do grupo *</Label>
                <Input
                    value={name}
                    onChange={e => setName(e.target.value)}
                    placeholder='Ex: Ponto da Carne'
                    autoFocus
                />
            </Field>

            <Row>
                <Field style={{ flex: 1, minWidth: 0 }}>
                    <Label>Mín. seleções</Label>
                    <Input
                        type='number'
                        min='0'
                        value={minSelections}
                        onChange={e => setMinSelections(e.target.value)}
                    />
                </Field>
                <Field style={{ flex: 1, minWidth: 0 }}>
                    <Label>Máx. seleções</Label>
                    <Input
                        type='number'
                        min='1'
                        value={maxSelections}
                        onChange={e => setMaxSelections(e.target.value)}
                    />
                </Field>
            </Row>

            <Field style={{ maxWidth: 120 }}>
                <Label>Ordem</Label>
                <Input
                    type='number'
                    min='0'
                    value={sortOrder}
                    onChange={e => setSortOrder(e.target.value)}
                    placeholder='Ex: 1'
                />
            </Field>

            <ToggleRow>
                <ToggleSwitch
                    $active={isRequired}
                    onClick={() => setIsRequired(r => !r)}
                    type='button'
                    role='switch'
                    aria-checked={isRequired}
                    aria-label='Grupo obrigatório'
                >
                    <ToggleThumb $active={isRequired} />
                </ToggleSwitch>
                <ToggleLabelGroup onClick={() => setIsRequired(r => !r)}>
                    <ToggleLabel>Obrigatório</ToggleLabel>
                    <ToggleHint>{isRequired ? 'Clientes devem selecionar neste grupo' : 'Seleção é livre'}</ToggleHint>
                </ToggleLabelGroup>
            </ToggleRow>

            <OptionsSection>
                <SectionTitle>Opções</SectionTitle>
                {options.map((opt, i) => (
                    <OptionInputRow key={opt.id ?? `new-${i}`}>
                        <Input
                            placeholder='Nome da opção'
                            value={opt.name}
                            onChange={e => updateOptionField(i, 'name', e.target.value)}
                            style={{ flex: 2 }}
                        />
                        <PriceWrapper style={{ flex: 1 }}>
                            <CurrencyPrefix>+R$</CurrencyPrefix>
                            <PriceInput
                                type='number'
                                step='0.01'
                                min='0'
                                value={opt.price_adjustment}
                                onChange={e => updateOptionField(i, 'price_adjustment', e.target.value)}
                                placeholder='0'
                            />
                        </PriceWrapper>
                        {options.length > 1 && (
                            <RemoveBtn type='button' onClick={() => removeOption(i)}>
                                <Trash2 size={14} color={colors.danger} />
                            </RemoveBtn>
                        )}
                    </OptionInputRow>
                ))}
                <AddOptionBtn type='button' onClick={addOptionRow}>
                    <Plus size={14} /> Adicionar opção
                </AddOptionBtn>
            </OptionsSection>

            {error && <ErrorText>{error}</ErrorText>}

            <SubmitBtn type='submit' disabled={loading}>
                {loading ? 'Salvando...' : initialData ? 'Salvar alterações' : 'Criar grupo'}
            </SubmitBtn>
        </Form>
    );
}

/* Styled components */
const Form = styled.form`display: flex; flex-direction: column; gap: 16px;`;
const Field = styled.div`display: flex; flex-direction: column; gap: 6px;`;
const Label = styled.label`font-size: 13px; font-weight: 600; color: ${colors.nightDark};`;
const Input = styled.input`
    border: 1.5px solid ${colors.neutralDark}; border-radius: 10px; padding: 12px 14px;
    font-size: 15px; outline: none; color: ${colors.nightDark}; background: #fff;
    transition: border-color 0.15s; box-sizing: border-box;
    &:focus { border-color: ${panelAccent(colors.occasionalPurple)}; }
    &::placeholder { color: #999; }
`;
const Row = styled.div`display: flex; gap: 12px;`;

const ToggleRow = styled.div`display: flex; align-items: center; gap: 10px;`;
const ToggleLabelGroup = styled.div`display: flex; flex-direction: column; gap: 1px; cursor: pointer; user-select: none;`;
const ToggleHint = styled.span`font-size: 11px; color: ${colors.nightLight};`;
const ToggleSwitch = styled.button`
    width: 44px; height: 24px; border-radius: 12px; border: none; padding: 2px;
    background: ${({ $active }) => $active ? panelAccent(colors.occasionalPurple) : colors.neutralDark};
    cursor: pointer; display: flex; align-items: center;
    transition: background 0.2s;
`;
const ToggleThumb = styled.div`
    width: 20px; height: 20px; border-radius: 50%; background: #fff;
    transform: ${({ $active }) => $active ? 'translateX(20px)' : 'translateX(0)'};
    transition: transform 0.2s;
    box-shadow: 0 1px 3px rgba(0,0,0,0.2);
`;
const ToggleLabel = styled.span`font-size: 14px; font-weight: 600; color: ${colors.nightDark};`;

const OptionsSection = styled.div`display: flex; flex-direction: column; gap: 8px;`;
const SectionTitle = styled.p`font-size: 13px; font-weight: 600; color: ${colors.nightDark}; margin: 0;`;
const OptionInputRow = styled.div`display: flex; gap: 8px; align-items: center;`;

const PriceWrapper = styled.div`
    display: flex; align-items: center;
    border: 1.5px solid ${colors.neutralDark}; border-radius: 10px; background: #fff;
    overflow: hidden; transition: border-color 0.15s;
    &:focus-within { border-color: ${panelAccent(colors.occasionalPurple)}; }
`;
const CurrencyPrefix = styled.span`
    padding: 12px 8px 12px 12px; font-size: 13px; color: #666;
    font-weight: 600; border-right: 1px solid ${colors.neutralDark}; background: ${colors.neutralDark};
    white-space: nowrap;
`;
const PriceInput = styled.input`
    flex: 1; border: none; padding: 12px 10px; font-size: 14px; outline: none;
    color: ${colors.nightDark}; background: transparent; min-width: 0;
`;

const RemoveBtn = styled.button`background: none; border: none; padding: 6px; cursor: pointer; flex-shrink: 0;`;
const AddOptionBtn = styled.button`
    display: flex; align-items: center; gap: 6px; font-size: 13px;
    color: ${panelAccent(colors.occasionalPurple)}; background: none; border: none; cursor: pointer; padding: 0; font-weight: 600;
`;

const ErrorText = styled.p`color: ${colors.danger}; font-size: 13px; margin: 0;`;
const SubmitBtn = styled.button`
    padding: 14px; border-radius: 10px;
    background: ${({ disabled }) => disabled ? colors.neutralDark : panelAccent(colors.occasionalPurple)};
    color: ${({ disabled }) => disabled ? colors.nightLight : '#fff'};
    font-size: 15px; font-weight: 600; border: none;
    cursor: ${({ disabled }) => disabled ? 'not-allowed' : 'pointer'};
    &:hover:not(:disabled) { opacity: 0.9; }
`;
